import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getAnalyticsExportSectionOptions } from '../constants/analyticsExportDomains';
import { createAnalyticsExport } from '../store/features/postAnalyticsExport';

/**
 * @param {'overview'|'chat'|'ticket'|'agent'|'kb'} domain
 * @param {string} currentRange e.g. 7d, today, custom
 * @param {{ startDate?: string, endDate?: string }} dateRange
 * @param {string|null} [groupId]
 */
export function useAnalyticsExport(domain, currentRange, dateRange, groupId) {
  const dispatch = useDispatch();
  const exportLoading = useSelector((s) => s.analyticsExport.loading);

  const exportSectionOptions = useMemo(
    () => getAnalyticsExportSectionOptions(domain),
    [domain]
  );

  const [exportSection, setExportSection] = useState(
    () => exportSectionOptions[0]?.value ?? ''
  );

  useEffect(() => {
    const first = exportSectionOptions[0]?.value;
    if (first && !exportSectionOptions.some((o) => o.value === exportSection)) {
      setExportSection(first);
    }
  }, [exportSectionOptions, exportSection]);

  const handleExport = useCallback(() => {
    if (!exportSection) {
      toast.error('Select what to export.');
      return;
    }
    dispatch(
      createAnalyticsExport({
        domain,
        section: exportSection,
        currentRange,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        group_id: groupId || undefined,
      })
    )
      .unwrap()
      .then((res) => {
        const row = res?.data ?? res;
        const url = row?.download_url || row?.file_url;
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
          toast.success('Export ready.');
        } else {
          toast.error('Export did not return a download URL.');
        }
      })
      .catch((err) => {
        const msg =
          (typeof err === 'object' && err?.message) ||
          (typeof err === 'string' ? err : null) ||
          'Export failed';
        toast.error(String(msg));
      });
  }, [
    dispatch,
    domain,
    exportSection,
    currentRange,
    dateRange?.startDate,
    dateRange?.endDate,
    groupId,
  ]);

  return {
    exportSectionOptions,
    exportSection,
    setExportSection,
    handleExport,
    exportLoading,
  };
}
